/**
 * QuickBooks integration hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { QBOConnectionSafe, QBOSyncRun } from '@/types/domain'

/**
 * Get QuickBooks connection status for a client
 */
export function useQBOConnection(clientId: string | undefined) {
  return useQuery({
    queryKey: ['qbo-connection', clientId],
    queryFn: async () => {
      if (!clientId) return null

      const { data, error } = await supabase
        .from('qbo_connections_safe')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      return data as QBOConnectionSafe | null
    },
    enabled: !!clientId,
  })
}

/**
 * Get sync runs for a client
 */
export function useQBOSyncRuns(clientId: string | undefined, limit = 10) {
  return useQuery({
    queryKey: ['qbo-sync-runs', clientId, limit],
    queryFn: async () => {
      if (!clientId) return []

      const { data, error } = await supabase
        .from('qbo_sync_runs')
        .select('*')
        .eq('client_id', clientId)
        .order('started_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data as QBOSyncRun[]
    },
    enabled: !!clientId,
  })
}

/**
 * Get detected payments for a client
 */
export function useQBODetectedPayments(clientId: string | undefined) {
  return useQuery({
    queryKey: ['qbo-detected-payments', clientId],
    queryFn: async () => {
      if (!clientId) return []

      const { data, error } = await supabase.rpc('get_qbo_detected_payments', {
        p_client_id: clientId,
      } as any)

      if (error) throw error
      return data || []
    },
    enabled: !!clientId,
  })
}

/**
 * Initiate QuickBooks connection
 */
export function useQBOConnect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qbo-connect`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ client_id: clientId }),
        }
      )

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to initiate QuickBooks connection')
      }

      return result
    },
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: ['qbo-connection', clientId] })
    },
  })
}

/**
 * Sync QuickBooks data
 */
export function useQBOSync() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qbo-sync`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ client_id: clientId }),
        }
      )

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Sync failed')
      }

      return result
    },
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: ['qbo-connection', clientId] })
      queryClient.invalidateQueries({ queryKey: ['qbo-sync-runs', clientId] })
      queryClient.invalidateQueries({ queryKey: ['qbo-detected-payments', clientId] })
      queryClient.invalidateQueries({ queryKey: ['customers', clientId] })
      queryClient.invalidateQueries({ queryKey: ['invoices', clientId] })
    },
  })
}

/**
 * Disconnect QuickBooks
 */
export function useQBODisconnect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (clientId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qbo-disconnect`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ client_id: clientId }),
        }
      )

      const result = await response.json()
      if (!result.success) {
        throw new Error(result.error || 'Failed to disconnect QuickBooks')
      }

      return result
    },
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: ['qbo-connection', clientId] })
      queryClient.invalidateQueries({ queryKey: ['qbo-sync-runs', clientId] })
      queryClient.invalidateQueries({ queryKey: ['qbo-detected-payments', clientId] })
    },
  })
}

/**
 * Confirm a detected payment - creates the real payment record
 */
export function useConfirmDetectedPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      detectedPayment,
      paymentData,
      clientId
    }: {
      detectedPayment: any
      paymentData: any
      clientId: string
    }) => {
      void clientId // Used in onSuccess callback
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      // Use the real payment date from QuickBooks (or user override)
      const finalPaymentData = {
        ...paymentData,
        payment_date: paymentData.payment_date || detectedPayment.payment_date || new Date().toISOString().split('T')[0],
      }

      // Create the payment record (fees calculated automatically in PaymentForm)
      const { data: payment, error: paymentError } = await supabase
        .from('payments_received')
        .insert(finalPaymentData)
        .select()
        .single()

      if (paymentError) throw paymentError

      // Mark detected payment as confirmed
      const { error: confirmError } = await (supabase
        .from('qbo_detected_payments')
        .update as any)({
          status: 'confirmed',
          resolved_by: user.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', detectedPayment.id)

      if (confirmError) throw confirmError

      return payment
    },
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['qbo-detected-payments', clientId] })
      queryClient.invalidateQueries({ queryKey: ['payments', clientId] })
      queryClient.invalidateQueries({ queryKey: ['invoices', clientId] })
    },
  })
}

/**
 * Dismiss a detected payment
 */
export function useDismissDetectedPayment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ paymentId, clientId }: { paymentId: string; clientId: string }) => {
      void clientId // Used in onSuccess callback
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { error } = await (supabase
        .from('qbo_detected_payments')
        .update as any)({
          status: 'dismissed',
          resolved_by: user.id,
          resolved_at: new Date().toISOString(),
        })
        .eq('id', paymentId)

      if (error) throw error
    },
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['qbo-detected-payments', clientId] })
    },
  })
}
